/**
 * Die WebSocket-Verbindung zum Dienst.
 *
 * **Lebenszyklus, sonst nichts**: verbinden, anmelden, lesen, wiederverbinden. Was eine
 * Nachricht *bedeutet*, entscheidet `view.ts`; was legal ist, entscheidet der Server. Diese
 * Trennung ist der Grund, warum beides ohne Netz prüfbar ist — Socket und Planer kommen von
 * außen herein, wie die Uhr im Server und der Zufall in der Engine.
 *
 * **Die Anmeldung passiert von selbst.** Der Sitzungs-Token ist die Identität der Verbindung
 * (§46); ihn nachträglich zu schicken wäre eine Verbindung, die kurz niemandem gehört. Er steht
 * in der **Nachricht** und nicht in der Adresse — dort landete er in Logs und im `Referer`
 * (`apps/server/src/app.ts`).
 *
 * **Was nicht auf das Schema passt, kommt nicht durch** (Regel 7: in beide Richtungen
 * validiert). Eine unlesbare Nachricht wird verworfen und reißt die Verbindung **nicht** ab:
 * ein einzelnes kaputtes Paket darf keine laufende Partie beenden.
 *
 * **Ein Abbruch verbindet neu, mit wachsendem Abstand.** Ohne Abstand wäre ein neu startender
 * Server einem Ansturm aus allen offenen Browsern ausgesetzt — genau dann, wenn er am wenigsten
 * davon hat. Nach einer gelungenen Verbindung beginnt der Abstand von vorn, sonst wartet ein
 * Spieler nach einem kurzen Netzausfall am Abend zehn Sekunden auf jede Wiederverbindung.
 *
 * **Eine abgewiesene Anmeldung verbindet nicht neu.** Ein ungültiger Token wird durch
 * Wiederholen nicht gültig; es wäre ein Client, der sich selbst zum Angreifer macht. Der
 * Dispatch schließt mit 1008 („policy violation"), und das ist hier die einzige Unterscheidung,
 * die zählt.
 */

import { parseServerMessage, type ClientMessage, type ServerMessage } from '@flaghex/contracts';

/** Die Form, die der Browser-`WebSocket` ohnehin hat — als Vertrag, damit ein Test ihn ersetzt. */
export interface SocketLike {
  send: (data: string) => void;
  close: (code?: number, reason?: string) => void;
  onopen: (() => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code: number }) => void) | null;
  onerror: (() => void) | null;
}

export type ConnectionStatus = 'connecting' | 'open' | 'closed' | 'unauthenticated';

export interface ConnectionOptions {
  readonly sessionToken: string;
  /** Vorbelegt aus der Herkunft der Seite. Hereingegeben nur für Tests. */
  readonly url?: string;
  readonly openSocket?: (url: string) => SocketLike;
  /** Der Planer für den nächsten Versuch; gibt zurück, wie man ihn zurücknimmt. */
  readonly schedule?: (run: () => void, delayMs: number) => () => void;
  readonly onMessage: (message: ServerMessage) => void;
  readonly onStatus: (status: ConnectionStatus) => void;
}

export interface Connection {
  /**
   * Schickt eine Absicht und gibt ihre Kennung zurück — oder `undefined`, wenn nichts offen ist.
   *
   * Nicht zu senden ist hier die richtige Antwort und kein Fehler: der Server ist autoritativ,
   * eine nicht gesendete Absicht ist eine, die nicht passiert ist. Die Oberfläche weiß am
   * Status, dass sie nichts anbieten darf.
   */
  readonly send: (message: ClientMessage) => string | undefined;
  readonly close: () => void;
}

/** Der Schließcode, mit dem der Dispatch eine abgewiesene Anmeldung beendet. */
const CLOSE_UNAUTHENTICATED = 1008;

/** Der erste Abstand und die Obergrenze. Verdoppelt wird dazwischen. */
const FIRST_RETRY_MS = 500;
const MAX_RETRY_MS = 10_000;

/**
 * Die Adresse des Endpunkts, abgeleitet aus der Herkunft der Seite.
 *
 * `wss` auf einer `https`-Seite ist Pflicht: ein `ws://` blockiert jeder Browser, und zwar
 * stumm. Die Partie steht **nicht** in der Adresse — sie folgt aus dem Sitzungs-Token.
 */
export function websocketUrl(location: { protocol: string; host: string }): string {
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${location.host}/api/v1/ws`;
}

export function createConnection(options: ConnectionOptions): Connection {
  const url = options.url ?? websocketUrl(window.location);
  const open = options.openSocket ?? ((target: string) => new WebSocket(target) as SocketLike);
  const schedule =
    options.schedule ??
    ((run, delayMs) => {
      const handle = setTimeout(run, delayMs);
      return () => clearTimeout(handle);
    });

  let socket: SocketLike | undefined;
  let status: ConnectionStatus = 'closed';
  let retryMs = FIRST_RETRY_MS;
  let cancelRetry: (() => void) | undefined;
  let stopped = false;
  let requests = 0;

  function report(next: ConnectionStatus): void {
    if (next === status) {
      return;
    }
    status = next;
    options.onStatus(next);
  }

  function connect(): void {
    if (stopped) {
      return;
    }
    report('connecting');
    const current = open(url);
    socket = current;

    current.onopen = () => {
      // Erst nach einer **gelungenen** Verbindung zurücksetzen: sonst wäre der wachsende
      // Abstand wirkungslos, sobald ein Server beim Verbinden sofort wieder abbricht.
      retryMs = FIRST_RETRY_MS;
      report('open');
      current.send(JSON.stringify({ type: 'AUTHENTICATE', sessionToken: options.sessionToken }));
    };

    current.onmessage = (event) => {
      let parsed: ServerMessage;
      try {
        parsed = parseServerMessage(JSON.parse(String(event.data)));
      } catch {
        // Verworfen, nicht geschlossen: ein einzelnes kaputtes Paket darf keine laufende
        // Partie beenden.
        return;
      }
      options.onMessage(parsed);
    };

    // `onerror` kommt vor `onclose` und trägt keine Auskunft. Behandelt wird der Abbruch.
    current.onerror = null;

    current.onclose = (event) => {
      socket = undefined;
      if (stopped) {
        report('closed');
        return;
      }
      if (event.code === CLOSE_UNAUTHENTICATED) {
        stopped = true;
        report('unauthenticated');
        return;
      }
      report('closed');
      const delay = retryMs;
      retryMs = Math.min(retryMs * 2, MAX_RETRY_MS);
      cancelRetry = schedule(() => {
        cancelRetry = undefined;
        connect();
      }, delay);
    };
  }

  connect();

  return {
    send: (message) => {
      if (socket === undefined || status !== 'open') {
        return undefined;
      }
      const requestId = `r-${(requests += 1)}`;
      socket.send(JSON.stringify({ ...message, requestId }));
      return requestId;
    },

    close: () => {
      stopped = true;
      cancelRetry?.();
      cancelRetry = undefined;
      socket?.close(1000, 'bye');
      socket = undefined;
      report('closed');
    },
  };
}
